import { escapeHtml } from './helpers';
import { escapeHtmlAttr, resolveEmailImageUrl } from './emailAssetUrl';
import { resolveHeaderAccentColor, resolveHeaderNavColor } from './headerResponsiveCss';

type HeaderLink = { label?: string; url?: string };

export type HeaderBlockContent = {
  layout?: string;
  logoUrl?: string;
  logoMaxWidth?: string;
  links?: HeaderLink[];
  navFontWeight?: string;
  navTextColor?: string;
  accentLinkColor?: string;
  actionText?: string;
  actionUrl?: string;
  actionButtonBg?: string;
  actionButtonColor?: string;
};

export type HeaderBlockStyles = {
  padding?: string;
  backgroundColor?: string;
};

const renderNavLinksHtml = (
  links: HeaderLink[],
  layout: string,
  fontFamily: string,
  baseColor: string,
  navFontWeight: string,
  accentColor?: string
): string => {
  return links
    .map((link, index) => {
      const accent = resolveHeaderAccentColor(layout, index, accentColor);
      const linkColor = accent || baseColor;
      const uppercase = layout === 'bold-centered';
      return `
        <td class="header-nav-cell" style="padding: 0 12px; width: auto !important;">
          <a href="${escapeHtml(link.url || '#')}" class="header-nav-link" style="color: ${linkColor}; font-weight: ${navFontWeight}; text-decoration: none; font-size: 14px; font-family: ${fontFamily}; text-transform: ${uppercase ? 'uppercase' : 'none'}; letter-spacing: ${uppercase ? '1px' : 'normal'}; white-space: nowrap;">
            ${escapeHtml(link.label || 'Link')}
          </a>
        </td>`;
    })
    .join('');
};

export const renderHeaderBlockHtml = (
  content: HeaderBlockContent,
  styles: HeaderBlockStyles,
  fontFamily: string,
  assetOrigin?: string,
  isMobile = false
): string => {
  const layout = content.layout || 'modern-clean';
  const logoUrl = escapeHtmlAttr(
    resolveEmailImageUrl(content.logoUrl || '/images/Email Builder/DEFAULT LOGO.png', assetOrigin)
  );
  const logoMaxWidth = content.logoMaxWidth || '120px';
  const links = Array.isArray(content.links) ? content.links : [];
  const navFontWeight = content.navFontWeight || '600';
  const bg = styles.backgroundColor || '#FFFFFF';
  const navColor = resolveHeaderNavColor(bg, content.navTextColor, layout);
  const actionText = escapeHtml(content.actionText || '');
  const actionUrl = escapeHtml(content.actionUrl || '#');
  const actionBtnBg = content.actionButtonBg || '#FFFFFF';
  const actionBtnColor = content.actionButtonColor || '#1E293B';
  const padding = isMobile ? '16px' : (styles.padding || '20px');

  const logoImgCentered = `<img src="${logoUrl}" alt="Logo" style="max-width: ${logoMaxWidth}; height: auto; display: block; border: 0; margin: 0 auto;" />`;
  const logoImgInline = `<img src="${logoUrl}" alt="Logo" style="max-width: ${logoMaxWidth}; height: auto; display: block; border: 0; margin: 0 auto;" />`;

  let innerHtml = '';

  // Mobile: single centered stack (matches builder canvas)
  if (isMobile) {
    const navRow = renderNavLinksHtml(links, layout, fontFamily, navColor, navFontWeight, content.accentLinkColor);
    const ctaHtml =
      layout === 'action' && actionText
        ? `<a href="${actionUrl}" class="header-action-btn" style="display: inline-block; background-color: ${actionBtnBg}; color: ${actionBtnColor}; padding: 10px 18px; border-radius: 4px; text-decoration: none; font-weight: 700; font-size: 13px; font-family: ${fontFamily}; white-space: nowrap;">${actionText}</a>`
        : '';

    innerHtml = `
      <table class="header-mobile-stack" role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr>
          <td class="header-mobile-logo" align="center" style="padding-bottom: 12px; text-align: center;">
            ${logoImgCentered}
          </td>
        </tr>
        <tr>
          <td class="header-mobile-nav" align="center" style="padding-bottom: ${ctaHtml ? '12px' : '0'}; text-align: center;">
            <table class="header-nav-row-table" role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" style="margin: 0 auto; width: auto;">
              <tr class="header-nav-row">${navRow}</tr>
            </table>
          </td>
        </tr>
        ${
          ctaHtml
            ? `<tr>
          <td class="header-mobile-cta" align="center" style="text-align: center;">
            ${ctaHtml}
          </td>
        </tr>`
            : ''
        }
      </table>
    `;
  } else if (layout === 'bold-centered' || layout === 'dark-accent') {
    innerHtml = `
      <table class="header-centered-table" role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr>
          <td class="header-centered-logo-cell" align="center" style="padding-bottom: 20px; text-align: center;">
            ${logoImgCentered}
          </td>
        </tr>
        <tr>
          <td align="center">
            <table class="header-nav-row-table" role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" style="width: auto !important; margin: 0 auto;">
              <tr class="header-nav-row">
                ${renderNavLinksHtml(links, layout, fontFamily, navColor, navFontWeight, content.accentLinkColor)}
              </tr>
            </table>
          </td>
        </tr>
      </table>
    `;
  } else if (layout === 'action') {
    const navLinks = renderNavLinksHtml(links, layout, fontFamily, navColor, navFontWeight);
    const ctaHtml = actionText
      ? `<a href="${actionUrl}" class="header-action-btn" style="display: inline-block; background-color: ${actionBtnBg}; color: ${actionBtnColor}; padding: 10px 20px; border-radius: 4px; text-decoration: none; font-weight: 700; font-size: 14px; font-family: ${fontFamily}; white-space: nowrap;">${actionText}</a>`
      : '';

    innerHtml = `
      <table class="header-action-table" role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr class="header-action-row">
          <td class="header-action-logo" align="left" valign="middle" width="28%" style="width: 28%; vertical-align: middle; text-align: left;">
            ${logoImgInline}
          </td>
          <td class="header-action-nav" align="center" valign="middle" width="44%" style="width: 44%; vertical-align: middle;">
            <table class="header-nav-row-table" role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" style="margin: 0 auto; width: auto;">
              <tr class="header-nav-row">${navLinks}</tr>
            </table>
          </td>
          <td class="header-action-cta" align="right" valign="middle" width="28%" style="width: 28%; vertical-align: middle;">
            ${ctaHtml}
          </td>
        </tr>
      </table>
    `;
  } else {
    innerHtml = `
      <table class="header-modern-clean-table" role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr>
          <td class="header-modern-clean-logo" align="left" valign="middle" width="40%" style="vertical-align: middle; text-align: left;">
            ${logoImgInline}
          </td>
          <td class="header-modern-clean-nav" align="right" valign="middle" width="60%" style="vertical-align: middle;">
            <table class="header-nav-row-table" role="presentation" cellspacing="0" cellpadding="0" border="0" align="right" style="margin-left: auto; width: auto;">
              <tr class="header-nav-row">
                ${renderNavLinksHtml(links, layout, fontFamily, navColor, navFontWeight)}
              </tr>
            </table>
          </td>
        </tr>
      </table>
    `;
  }

  const wrapClass =
    layout === 'bold-centered' || layout === 'dark-accent'
      ? 'header-block-wrap header-centered-wrap'
      : 'header-block-wrap';

  return `
    <table role="presentation" class="${wrapClass}" cellspacing="0" cellpadding="0" border="0" width="100%" style="width: 100%; border-collapse: collapse; background-color: ${bg};">
      <tr>
        <td style="padding: ${padding}; background-color: ${bg}; font-family: ${fontFamily}; width: 100%; box-sizing: border-box;">
          ${innerHtml}
        </td>
      </tr>
    </table>
  `;
};
